import proj4 from 'proj4';

// Register Georgia State Plane coordinate systems (NAD83, US Survey Feet)
proj4.defs('EPSG:2239', '+proj=tmerc +lat_0=30 +lon_0=-82.16666666666667 +k=0.9999 +x_0=200000.0000000002 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=us-ft +no_defs'); // Georgia East
proj4.defs('EPSG:2240', '+proj=tmerc +lat_0=30 +lon_0=-84.16666666666667 +k=0.9999 +x_0=700000.0000000001 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=us-ft +no_defs'); // Georgia West

/**
 * Auto-detect Georgia State Plane zone based on longitude
 * Boundary is approximately -83.5 degrees
 */
export function detectZone(lng) {
  return lng < -83.5 ? 'west' : 'east';
}

/**
 * Convert lat/lng (WGS84) to Georgia State Plane coordinates
 * Returns { northing, easting } in US Survey Feet
 */
export function toStatePlane(lat, lng, zone = 'west') {
  const epsg = zone === 'east' ? 'EPSG:2239' : 'EPSG:2240';
  const [easting, northing] = proj4('EPSG:4326', epsg, [lng, lat]);
  return { northing, easting };
}

/**
 * Convert Georgia State Plane coordinates to lat/lng (WGS84)
 * Input: northing and easting in US Survey Feet
 */
export function fromStatePlane(northing, easting, zone = 'west') {
  const epsg = zone === 'east' ? 'EPSG:2239' : 'EPSG:2240';
  const [lng, lat] = proj4(epsg, 'EPSG:4326', [easting, northing]);
  return { lat, lng };
}

/**
 * Format a State Plane coordinate value with commas and 2 decimal places
 */
export function formatStatePlane(value) {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Format lat/lng to specified decimal places
 */
export function formatLatLng(value, decimals = 6) {
  return value.toFixed(decimals);
}

/**
 * Format decimal degrees as DMS (e.g., 34 3'7.92"N)
 */
export function formatDMS(decimalDegrees, isLat) {
  const absolute = Math.abs(decimalDegrees);
  const degrees = Math.floor(absolute);
  const minutesDecimal = (absolute - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = ((minutesDecimal - minutes) * 60).toFixed(2);
  const direction = isLat
    ? (decimalDegrees >= 0 ? 'N' : 'S')
    : (decimalDegrees >= 0 ? 'E' : 'W');
  return `${degrees}\u00B0${minutes}'${seconds}"${direction}`;
}

/**
 * Parse coordinate input - handles decimal degrees and DMS
 * Returns { value: number, valid: boolean }
 */
export function parseCoordinateInput(input) {
  if (!input || !input.trim()) return { value: 0, valid: false };

  const trimmed = input.trim();

  // Try decimal degrees first (e.g., "34.0522" or "-84.5")
  const decimal = parseFloat(trimmed);
  if (!isNaN(decimal) && /^-?\d+\.?\d*$/.test(trimmed)) {
    return { value: decimal, valid: true };
  }

  // Try DMS format: "34 3 7.92 N" or "34d3m7.92sN" or "34 03 07.92N"
  const dmsRegex = /^(-?\d+)[d\s\u00B0]+(\d+)[m'\s]+(\d+\.?\d*)[s"\s]*([NSEWnsew])?$/;
  const match = trimmed.match(dmsRegex);
  if (match) {
    const deg = parseInt(match[1]);
    const min = parseInt(match[2]);
    const sec = parseFloat(match[3]);
    const dir = (match[4] || '').toUpperCase();

    let value = Math.abs(deg) + min / 60 + sec / 3600;
    if (dir === 'S' || dir === 'W' || deg < 0) {
      value = -value;
    }
    return { value, valid: true };
  }

  // Try simple negative with spaces: "-84 30 0"
  const simpleRegex = /^(-?\d+)\s+(\d+)\s+(\d+\.?\d*)$/;
  const simpleMatch = trimmed.match(simpleRegex);
  if (simpleMatch) {
    const deg = parseInt(simpleMatch[1]);
    const min = parseInt(simpleMatch[2]);
    const sec = parseFloat(simpleMatch[3]);
    let value = Math.abs(deg) + min / 60 + sec / 3600;
    if (deg < 0) value = -value;
    return { value, valid: true };
  }

  return { value: 0, valid: false };
}
